import React, { useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./FillInForm.styles.scss";
import { Assignment, User } from "types";
import {
  useGetPatients,
  useGetGroups,
  useGetGroupUsers,
  useCurrentUser,
} from "utils/hooks";
import {
  PatientsDropdown,
  AssignToggle,
  Button,
  BottomModal,
} from "components";
import FillAssignment from "./FillAssignment";
import AssignmentContextTherapistWrapper from "../../TherapistPages/AssignmentContextTherapistWrapper";
import { GROUP_USER_ROLES } from "utils/constants";
import {
  useOrganizationSettings,
  getPatientWord,
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";

interface FillInFormProps {
  assignment: Assignment;
  onClose: () => void;
}

const FillInForm = ({ assignment, onClose }: FillInFormProps) => {
  const { t } = useTranslation(["common"]);
  const { uuid } = useParams<{ uuid: string }>();
  const { patient } = useGetPatients({ slug: uuid });
  const { specificGroup: group } = useGetGroups({ slug: uuid });
  const { groupUsers } = useGetGroupUsers({ groupId: group?.uuid });
  const { user } = useCurrentUser();
  const history = useNavigate();
  const [visibleToPatient, setVisibleToPatient] = useState(false);
  const [requestPin, setRequestPin] = useState(user?.pinCode ? true : false);
  const [selectedPatient, setSelectedPatient] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [showFillInModal, setShowFillInModal] = useState(false);
  const { settings: orgSettings } = useOrganizationSettings();

  const groupPatients = useMemo(
    () =>
      groupUsers?.filter((user: User) => user.role === GROUP_USER_ROLES.MEMBER),
    [groupUsers]
  );

  const handleSubmitFillingIn = () => {
    if (!patient && group && !selectedPatient) {
      toast.warn(
        t("patients.choose_patient_warn", {
          patientWord: getPatientWord(orgSettings),
        })
      );
      return;
    }
    setShowFillInModal(true);
  };

  const onCloseFillInModal = () => {
    setSelectedPatient(null);
    setShowFillInModal(false);
    onClose();
    requestPin && history("/pin-verification");
  };

  return (
    <>
      <p className={`fill_title ${patient ? "mb" : ""}`}>
        <Trans
          i18nKey="common:workbook.fill_in_description"
          components={{ br: <br /> }}
          values={{
            patientWord: getPatientWord(orgSettings),
          }}
        />
      </p>
      {group && (
        <>
          <div className="fill_label mb">
            {t("workbook.choose_patient_label", {
              patientWordCapitalized: getPatientWordCapitalized(orgSettings),
            })}
          </div>
          <PatientsDropdown
            patientsList={groupPatients}
            placeholder={t("workbook.choose_placeholder")}
            onChange={(selected) => setSelectedPatient(selected)}
          />
        </>
      )}
      <div className="fill_toggle-container">
        <AssignToggle
          label={t("workbook.fill_in_submission_visible", {
            patientWord: getPatientWord(orgSettings),
          })}
          labelStyle={"fill_label"}
          checked={visibleToPatient}
          onChange={(e) => setVisibleToPatient(e.target.checked)}
        />
        <div className="fill_hint">
          <Trans
            i18nKey="common:workbook.fill_in_submiss_visible_hint"
            components={{ br: <br /> }}
            values={{
              patientWord: getPatientWord(orgSettings),
            }}
          />
        </div>
      </div>
      <div
        className={`fill_toggle-container ${user?.pinCode ? "" : "disabled"}`}
      >
        <AssignToggle
          label={t("workbook.fill_pin_label")}
          labelStyle={"fill_label"}
          checked={requestPin}
          onChange={(e) => setRequestPin(e.target.checked)}
        />
        <div className="fill_hint">
          <Trans
            i18nKey="common:workbook.fill_in_pin_hint"
            components={{ br: <br /> }}
            values={{
              patientWord: getPatientWord(orgSettings),
            }}
          />
        </div>
      </div>
      {!user?.pinCode && (
        <p className="fill_settings_hint">
          <Trans
            i18nKey={"common:profile.set_pin_hint"}
            components={{
              Button: (
                <Button
                  value={t("profile.user_settings")}
                  name="redirect-btn"
                  onClick={() => history("/settings/account")}
                />
              ),
            }}
          />
        </p>
      )}
      <Button
        name="submit_fill_in_assignment"
        value={t("workbook.fill_in_submit_btn")}
        pill={true}
        extraClass="submit-button b-fixed"
        onClick={() => handleSubmitFillingIn()}
      />

      <BottomModal
        isVisible={showFillInModal}
        title={assignment?.label}
        extraClass="fill-modal_container_color"
        handleClose={onCloseFillInModal}
      >
        <AssignmentContextTherapistWrapper patient={patient} group={group}>
          <FillAssignment
            singlePatient={patient}
            selectedPatient={selectedPatient}
            group={group}
            assignment={assignment}
            hideFromPatient={!visibleToPatient}
            requestPin={requestPin}
            onClose={onCloseFillInModal}
          />
        </AssignmentContextTherapistWrapper>
      </BottomModal>
    </>
  );
};

export default FillInForm;
